/**
 * ServedByLine — one-line trust microcopy shown under every FlightCard.
 *
 * Reads from `useServedByInfo(iata)`. Renders nothing until the
 * registry answer arrives (max ~50ms after mount). Never shows
 * unverified data: if the registry can't resolve the IATA, the
 * component silently returns null.
 *
 * Phase-2 Trust Engine — resolves user objections:
 *   • "Is this the right airport for my destination?"
 *   • "How do I get from the airport to the hotel?"
 * before the Book click.
 */
import React from 'react';
import { MapPin, Clock, Car, Briefcase } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useServedByInfo from '../../hooks/useServedByInfo';

export default function ServedByLine({ iata, testId }) {
  const { language } = useLanguage();
  const info = useServedByInfo(iata);
  if (!info || !info.town) return null;

  const T = language === 'bg' ? {
    servedBy: 'Обслужва се от',
    driveShort: 'мин до центъра',
    driveLong: 'ч до центъра',
    carsTaxis: 'Коли и трансфери',
  } : {
    servedBy: 'Served by',
    driveShort: 'min to city',
    driveLong: 'h to city',
    carsTaxis: 'Cars & transfers',
  };

  const driveLabel = (() => {
    if (typeof info.driveMinutes !== 'number' || info.driveMinutes <= 0) return null;
    if (info.driveMinutes < 90) return `${info.driveMinutes} ${T.driveShort}`;
    const h = Math.floor(info.driveMinutes / 60);
    const m = info.driveMinutes % 60;
    return m ? `${h}h ${m} ${T.driveShort.replace('min','').trim()}` : `${h} ${T.driveLong}`;
  })();

  return (
    <div
      data-testid={testId || `served-by-${iata}`}
      className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[10px] text-gray-500"
    >
      <span className="inline-flex items-center gap-1">
        <MapPin className="w-2.5 h-2.5 text-emerald-600" />
        <span className="font-medium text-gray-700">{T.servedBy} {info.town} ({info.iata})</span>
      </span>
      {driveLabel && (
        <>
          <span aria-hidden className="text-gray-300">·</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" />
            {driveLabel}
          </span>
        </>
      )}
      {(info.hasCars || info.hasTransfer) && (
        <>
          <span aria-hidden className="text-gray-300">·</span>
          <span className="inline-flex items-center gap-1">
            {info.hasCars && <Car className="w-2.5 h-2.5" />}
            {info.hasTransfer && <Briefcase className="w-2.5 h-2.5" />}
            {T.carsTaxis}
          </span>
        </>
      )}
    </div>
  );
}
